import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  redirect,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import mascotAsset from "@/assets/durafoam-mascot.png.asset.json";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { getRedirectFor } from "@/lib/redirects";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MobileStickyBar } from "@/components/site/MobileStickyBar";
import { SkipToContent } from "@/components/site/SkipToContent";
import { Toaster } from "@/components/ui/sonner";
import { MetaPixel } from "@/components/site/MetaPixel";
import { OpenAiPixel } from "@/components/site/OpenAiPixel";
import { DeferredHeadAssets } from "@/components/site/DeferredHeadAssets";
import { ChatLauncher } from "@/components/chat/ChatLauncher";
import { InspectionDialogProvider } from "@/components/site/InspectionDialog";

import { businessNode } from "@/lib/seo";

const businessSchema = businessNode();


function NotFoundComponent() {
  return (
    <div className="bg-background px-4 py-16 lg:py-24">
      <div className="mx-auto max-w-3xl text-center">
        <meta name="robots" content="noindex" />
        <img
          src={mascotAsset.url}
          alt="Durafoam Roofing mascot"
          width={120}
          height={192}
          className="mx-auto h-48 w-auto object-contain"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
        />
        <h1 className="mt-6 text-3xl font-bold text-primary sm:text-4xl">
          Looks like this page went off the roof.
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-[var(--muted-foreground)]">
          Don't worry — we find leaks for a living. Let's get you back on solid ground.
        </p>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            { to: "/", label: "Back to home", desc: "Return to the homepage" },
            { to: "/free-roof-inspection-phoenix", label: "Schedule a free inspection", desc: "We'll come take a look" },
            { to: "/services", label: "See all services", desc: "Browse our roofing services" },
          ].map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className="rounded-xl border border-border bg-white p-5 text-left transition hover:border-primary hover:shadow-sm"
            >
              <div className="text-sm font-semibold text-primary">{c.label}</div>
              <div className="mt-1 text-xs text-[var(--muted-foreground)]">{c.desc}</div>
            </Link>
          ))}
        </div>
        <p className="mt-8 text-sm text-[var(--muted-foreground)]">
          Or just call us:{" "}
          <a href="tel:+14809415373" className="font-semibold text-accent hover:underline">
            480-941-5373
          </a>
        </p>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-background px-4 py-20">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">This page didn't load</h1>
        <p className="mt-2 text-sm text-[var(--muted-foreground)]">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="inline-flex items-center justify-center rounded-lg bg-accent px-6 py-3 text-[15px] font-semibold text-white hover:bg-[var(--accent-hover)]"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-lg border-2 border-primary bg-background px-6 py-3 text-[15px] font-semibold text-primary hover:bg-primary hover:text-white"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  beforeLoad: async ({ location }): Promise<void> => {
    const { pathname, searchStr, hash } = location;
    // 0) Server-only: redirect scraper mirrors to the canonical host.
    if (typeof window === "undefined") {
      const { getIncomingHost } = await import("@/lib/canonical-host");
      const host = await getIncomingHost();
      const allowed =
        host === "" ||
        host === "durafoaminc.com" ||
        host === "www.durafoaminc.com" ||
        host.endsWith(".lovable.app") ||
        host === "localhost" ||
        host === "127.0.0.1";
      if (!allowed) {
        throw redirect({
          href: `https://www.durafoaminc.com${pathname}${searchStr ?? ""}${hash ? `#${hash}` : ""}`,
          statusCode: 301,
        });
      }
    }
    // 1) Legacy URL → permanent 301 to the closest matching page on the new site.
    const legacyTarget = getRedirectFor(pathname);
    if (legacyTarget && legacyTarget !== pathname) {
      throw redirect({
        href: `${legacyTarget}${searchStr ?? ""}${hash ? `#${hash}` : ""}`,
        statusCode: 301,
      });
    }
    // 2) Canonicalize trailing slashes.
    if (pathname.length > 1 && pathname.endsWith("/")) {
      const stripped = pathname.replace(/\/+$/, "") || "/";
      throw redirect({
        href: `${stripped}${searchStr ?? ""}${hash ? `#${hash}` : ""}`,
        statusCode: 301,
      });
    }
  },

  loader: async ({ context, location }) => {
    const { getSiteSettings } = await import("@/lib/site-settings.functions");
    const { PAGE_BY_PATH } = await import("@/lib/pages");
    const { getPageAssignments } = await import("@/lib/image-assignments.functions");


    const pathname = location.pathname === "/" ? "/" : location.pathname.replace(/\/+$/, "");
    const page = PAGE_BY_PATH[pathname];

    const settingsPromise = context.queryClient.ensureQueryData({
      queryKey: ["site-settings"],
      queryFn: () => getSiteSettings(),
      staleTime: 5 * 60 * 1000,
    });

    const assignmentsPromise = page
      ? context.queryClient
          .ensureQueryData({
            queryKey: ["image-assignments", page.slug],
            queryFn: () => getPageAssignments({ data: { pageSlug: page.slug } }),
            staleTime: 60_000,
          })
          .catch(() => [])
      : Promise.resolve([]);

    // Assignments are prefetched into the query cache above so ManagedImage
    // renders (and preloads) the hero during SSR without a second round trip.
    const [settings] = await Promise.all([settingsPromise, assignmentsPromise]);

    return {
      ga4: settings.ga4MeasurementId || "",
      gsc: settings.gscVerificationToken || "",
    };
  },
  head: ({ loaderData }) => {
    const gsc = loaderData?.gsc ?? "";
    const ga4 = loaderData?.ga4?.trim() ?? "";
    const meta: Array<Record<string, string>> = [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#1B2B6B" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Durafoam Roofing" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@Durafoam" },
    ];
    if (gsc) meta.push({ name: "google-site-verification", content: gsc });

    const scripts: Array<Record<string, string>> = [
      { type: "application/ld+json", children: JSON.stringify(businessSchema) },
    ];

    if (ga4) {
      scripts.unshift(
        {
          id: "ga4-script",
          async: "true",
          src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4)}`,
        },
        {
          id: "ga4-init",
          children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config',${JSON.stringify(ga4)},{send_page_view:true});`,
        },
      );
    }

    const links: Array<Record<string, string>> = [
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "stylesheet", href: appCss },
    ];
    // The hero preload is emitted by ManagedImage (eager) so it always matches
    // the crop/srcset actually rendered — a second preload here downloaded a
    // different aspect ratio of the same photo on every page load.
    return { meta, links, scripts };
  },

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="min-h-dvh bg-background text-foreground">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

/** Paid-ad landing pages render without site nav/footer/sticky bar. */
const BARE_LAYOUT_PATHS = ["/monsoon-roof-inspection"];

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const loaderData = Route.useLoaderData();
  const ga4 = loaderData?.ga4 || undefined;
  const routerLocation = useRouterState({ select: (s) => s.location });
  const path = (routerLocation?.pathname ?? "/").replace(/\/+$/, "") || "/";
  const bare = BARE_LAYOUT_PATHS.includes(path);

  if (bare) {
    return (
      <QueryClientProvider client={queryClient}>
        <SkipToContent />
        <main id="main">
          <Outlet />
        </main>
        <Toaster position="top-center" richColors />
        <MetaPixel />
        <OpenAiPixel />
        <DeferredHeadAssets ga4MeasurementId={ga4} />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <InspectionDialogProvider>
        <SkipToContent />
        <div className="flex min-h-dvh flex-col pb-14 lg:pb-0">
          <SiteHeader />
          <main id="main" className="flex-1">
            <Outlet />
          </main>
          <SiteFooter />
        </div>
        {path !== "/hail-damage-roof-inspection-phoenix" && <MobileStickyBar />}
        <ChatLauncher />

        <Toaster position="top-center" richColors />
        <MetaPixel />
        <OpenAiPixel />
        <DeferredHeadAssets ga4MeasurementId={ga4} />
      </InspectionDialogProvider>
    </QueryClientProvider>
  );
}

